import React, { useState } from 'react';
import classes from './AddProductModal.module.css';
import Modal from '../../../components/UI/Modals/Modal';
import { addCart, ICartProduct, updateCart } from '../../../store/slices/cart';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import PrimaryButtonV2 from '../../../components/UI/Buttons/PrimaryButtonV2';
import { CustomCalendar } from '../../../components/UI/CustomCalendar/CustomCalendar';
import moment from 'moment/moment';
import {
  getProductTimeSlots,
  ITimeSlotInfo,
  selectProductTimeSlots,
  selectProductTimeSlotsStatus,
  selectTimeSlots,
} from '../../../store/slices/auth';
import { Stepper } from '../../../components/UI/Stepper/Stepper';

import { IProductDetail } from '../../../store/slices/productCategories';
import { toast } from 'react-toastify';
import POSITION = toast.POSITION;
import WithLoader from '../../../components/WithLoader/WithLoader';
import { useTranslation } from '../../../hooks/useTranslations';

type Props = {
  product: IProductDetail;
  isOpen: boolean;
  onClose: () => void;
  editingCart?: ICartProduct;
  startStep?: number;
  index?: number;
};

const CustomCalendarModal: React.FC<Props> = ({
  product,
  isOpen,
  onClose,
  editingCart,
  startStep,
  index,
}) => {
  const DATE_FORMAT = 'DD.MM.yyyy';
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const timeSlots: ITimeSlotInfo[] = useAppSelector(selectTimeSlots);
  const productTimeSlots: ITimeSlotInfo[] = useAppSelector(selectProductTimeSlots);
  const productTimeSlotsStatus: string = useAppSelector(selectProductTimeSlotsStatus);
  const [activeStep, setActiveStep] = React.useState<number>(startStep ?? 0);
  const [activeContinue, setActiveContinue] = React.useState<boolean>(false);
  const [selectedDate, setSelectedDate] = React.useState<Date>(
    editingCart?.appointmentStart ? new Date(editingCart?.appointmentStart) : new Date()
  );
  const [appointmentDate, setAppointmentDate] = React.useState<Date | undefined>(
    editingCart?.appointmentStart
  );
  const [appointmentEndDate, setAppointmentEndDate] = React.useState<Date | undefined>(
    editingCart?.appointmentEnd
  );
  const [selectedLaboratoryLanguage] = useState<string>(
    product.languages && product.languages.length > 0 ? product.languages[0].join() : ''
  );

  const startDate = moment();
  const productId = product.product_id;
  const hasCalendarForProduct = product.product_calendar[0]?.product_calendar_items?.length > 0;
  React.useEffect(() => {
    if (!editingCart) {
      setSelectedDate(new Date());
      setAppointmentDate(new Date());
      setAppointmentEndDate(new Date());
      setActiveContinue(false);
    }
    const fetchTimeSlots = async () => {
      if (hasCalendarForProduct) {
        for (let i = 0; i < product.product_calendar[0].date_range_days; i++) {
          const timestamp = startDate.clone().add(i, 'days').format('YYYY-MM-DD');
          await dispatch(
            getProductTimeSlots({
              timeStamp: timestamp,
              product_id: productId,
            })
          );
        }
      }
    };
    fetchTimeSlots();
  }, [editingCart, isOpen]);

  const filterData = hasCalendarForProduct ? productTimeSlots : timeSlots;
  const filteredTimeSlots = filterData.filter((timeSlot) =>
    moment(timeSlot.start).startOf('day').isSame(moment(selectedDate).startOf('day'))
  );

  const groupByHour = React.useMemo(() => {
    const grouped: { timeSlots: ITimeSlotInfo[] }[] = [];
    for (let i = 0; i < filteredTimeSlots.length; i += 4) {
      grouped.push({
        timeSlots: filteredTimeSlots.slice(i, i + 4),
      });
    }
    return grouped;
  }, [filteredTimeSlots]);
  const highlightDates = Array.from(
    new Set(filterData.map((timeSlot) => moment(timeSlot.start).format(DATE_FORMAT)))
  );

  const handleUpdateCartItem = (index: number | undefined, type?: string) => {
    const updatedData = {
      appointmentStart: type === 'withoutAppointment' ? null : appointmentDate,
      appointmentEnd: type === 'withoutAppointment' ? null : appointmentEndDate,
    };
    dispatch(updateCart({ index, updatedData }));
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={activeStep === 0 && translations('choose.appointment').toString()}
      divider
      containerPadding={'20px'}
      maxWidth={'850px'}
      width={'70%'}
    >
      <WithLoader isLoading={productTimeSlotsStatus === 'pending'}>
        <div className={classes.container}>
          {product.product_group !== 'H' && (
            <Stepper
              steps={
                product.product_group !== 'F' && product.product_group !== 'C'
                  ? [{ displayText: translations('appointment').toString() }]
                  : [{ displayText: translations('appointment').toString() }]
              }
              activeStep={activeStep}
              setActiveStep={setActiveStep}
            />
          )}
          <div className={classes.content}>
            <div className={classes.appointmentContainer}>
              <CustomCalendar
                selectedDate={selectedDate}
                onSelectDay={(date) => setSelectedDate(date)}
                highlightedDates={highlightDates}
                format={DATE_FORMAT}
              />
              <div className={classes.appointments}>
                {groupByHour.length ? (
                  groupByHour.map((hour, index) => (
                    <div key={index} className={classes.row}>
                      {hour.timeSlots.map((timeSlot) => {
                        const startDate = moment(timeSlot.start);
                        return (
                          <div
                            key={`${index}_${timeSlot.start}`}
                            className={`${classes.timeSlot} ${
                              timeSlot.available < 1 || !moment().isBefore(startDate, 'minutes')
                                ? classes.disabledSlot
                                : startDate.isSame(moment(appointmentDate))
                                ? classes.selectedSlot
                                : ''
                            }`}
                            onClick={() => {
                              if (
                                timeSlot.available > 0 &&
                                moment().isBefore(startDate, 'minutes')
                              ) {
                                setAppointmentDate(timeSlot.start);
                                setAppointmentEndDate(timeSlot.end);
                                setActiveContinue(true);
                              }
                            }}
                          >
                            {startDate.format('HH:mm')}
                          </div>
                        );
                      })}
                    </div>
                  ))
                ) : (
                  <p>No visits available!</p>
                )}
              </div>
            </div>
          </div>
          <div className={classes.buttonsContainer}>
            {activeStep === 0 && product.product_group !== 'C' && (
              <PrimaryButtonV2
                text={translations('add.without.appointment')}
                invertedColors
                containerStyle={{
                  margin: 5,
                }}
                buttonStyle={{
                  height: 40,
                  fontSize: 16,
                  borderRadius: 7,
                  minWidth: 130,
                }}
                onClick={() => {
                  editingCart
                    ? handleUpdateCartItem(index, 'withoutAppointment')
                    : dispatch(
                        addCart({
                          product: product,
                          total: 0,
                          appointmentStart: '',
                          appointmentEnd: '',
                          laboratoryLanguage: selectedLaboratoryLanguage.split(','),
                          quantity: 1,
                        })
                      );
                  onClose();
                }}
              />
            )}
            {activeStep === 0 && (
              <PrimaryButtonV2
                text={translations('continue')}
                containerStyle={{
                  margin: 5,
                }}
                buttonStyle={{
                  height: 40,
                  fontSize: 16,
                  borderRadius: 7,
                  minWidth: 130,
                }}
                disabled={!activeContinue}
                onClick={() => {
                  editingCart
                    ? handleUpdateCartItem(index)
                    : dispatch(
                        addCart({
                          product: product,
                          total: 0,
                          appointmentStart: appointmentDate,
                          appointmentEnd: appointmentEndDate,
                          laboratoryLanguage: selectedLaboratoryLanguage.split(','),
                          quantity: 1,
                        })
                      );
                  onClose();
                  editingCart
                    ? toast.success(translations('cart.edited'), {
                        position: POSITION.BOTTOM_RIGHT,
                      })
                    : toast.success(translations('product.added'), {
                        position: POSITION.BOTTOM_RIGHT,
                      });
                }}
              />
            )}
            <PrimaryButtonV2
              text={translations('button.cancel')}
              invertedColors
              containerStyle={{
                margin: 5,
              }}
              buttonStyle={{
                height: 40,
                fontSize: 16,
                borderRadius: 7,
                minWidth: 130,
              }}
              onClick={onClose}
            />
          </div>
        </div>
      </WithLoader>
    </Modal>
  );
};

export { CustomCalendarModal };
